import { canToggleButtonChannelSplit } from "./buttonChannelSplitAvailability";
import type Button from "./Button";
import ButtonChannel, { mergeSingleButtonsToRocker, splitRockerToSingleButtons } from "./ButtonChannel";
import Channel, { ButtonSide } from "./Channel";
import type Device from "./Device";
import { ChannelType, areChannelTypesCompatible } from "./ChannelType";
import AllowedConections from "./AllowedConnections";
import { RockerConfig } from "./KnxRules";

export async function setChannelType(newChannelType: ChannelType, device: Device, channel: Channel) {
  const tag1 = channel.tag.substring(0, 3) + "R";
  const rightButtonChannel = device.channels.getItemByTag(tag1);

  async function setChannelType2(xButtonChannel: Channel, newChannelType: ChannelType) {
    ///////////////////////////////////////////////////////////////////////////////////////
    //
    // Force setting of channelType
    // Overrides even typeAdjustement ie "Switch" to "DisplayStatusSwitch"
    //
    xButtonChannel._channelType = ChannelType.Unknown;
    if (xButtonChannel.isButtonChannel) {
      (xButtonChannel as ButtonChannel).channelTypeIsSetByUser = true;
    }
    if (xButtonChannel.knxChannel) {
      xButtonChannel.knxChannel._channelType = ChannelType.Unknown;
    }
    //
    ///////////////////////////////////////////////////////////////////////////////////////

    xButtonChannel.channelType = newChannelType;

    if (device.template) {
      if (
        xButtonChannel.hasKnxChannelTypeProperty ||
        xButtonChannel.parentDevice?.template?.knxProductData.hasScript_mapChannelType
      ) {
        xButtonChannel.setKnxChannelTypePropertyValue(xButtonChannel.channelType, true);
      } else {
        if (xButtonChannel.isButtonChannel && device.isButton) {
          const rules = device.template.knxProductData.currentRules;
          if (rules && (xButtonChannel as ButtonChannel).mayCallSetRockerConfig) {
            rules.setRockerConfig(device as Button, xButtonChannel as ButtonChannel);
          }
        }
      }

      device.template.knxProductData.currentRules?.pullAllValues(device, {});
      await device.updateAllFromKnxMetadata();

      xButtonChannel.raiseOnChange("channelType");
      xButtonChannel.connections.forEach((con) => {
        const partner = con.getPartner(xButtonChannel);
        if (partner) {
          con.forbidden = !AllowedConections.isConnectionAllowed(xButtonChannel, partner, false);
          con.raiseOnChange("forbidden");
        }
      });
    }
  }

  await setChannelType2(channel, newChannelType);

  if (channel.isVirtualRocker) {
    if (channel.buttonSide == ButtonSide.both) {
      //
      // for virtual rockers set channelType of right button to the same value
      //
      if (rightButtonChannel) {
        if (channel.isVirtualRocker || rightButtonChannel.isVirtualRocker) {
          await setChannelType2(rightButtonChannel, newChannelType);
        }
      }
    }
  }
}


export async function toggleButtonChannelSplit(channel: ButtonChannel): Promise<void> {
  if (!canToggleButtonChannelSplit(channel)) return;
  if (channel.rockerConfig !== RockerConfig.rocker && channel.buttonSide === ButtonSide.right) {
    // Merging always retains the left channel, regardless of which half opened the menu.
    const left = channel.otherSideButtonOfRocker();
    if (!left || left.buttonSide !== ButtonSide.left || !canToggleButtonChannelSplit(left)) return;
    channel = left;
  }
  const device = channel.parent as Button;
  // Legacy shared channels may not have a stored rockerConfig yet.
  const isRocker = channel.buttonSide === ButtonSide.both;
  if (isRocker) {
    splitRockerToSingleButtons(channel as ButtonChannel);
  } else {
    mergeSingleButtonsToRocker(channel as ButtonChannel);
  }

  if (device.template?.knxProductData.currentRules) {
    const rules = device.template.knxProductData.currentRules;
    if (rules && (channel as ButtonChannel).mayCallSetRockerConfig) {
      ////////////////////////////////////////////////////////////////////////////////////////
      //
      // 07.11.2025
      if (!isRocker && channel.channelType == ChannelType.Scene) {
        // we are merging to rocker
        // change channeltype if channelType is scene (rocker dont support scene)
        let newChannelType = ChannelType.Switch;
        const allowedChannelTypes = channel.allowedChannelTypes?.filter(
          (act) => act.channelType != ChannelType.Scene,
        );
        if (allowedChannelTypes && allowedChannelTypes?.length > 0) {
          newChannelType = allowedChannelTypes[0].channelType;
        }
        await setChannelType(newChannelType, device, channel as ButtonChannel);
      }
      ////////////////////////////////////////////////////////////////////////////////////////
      rules.setRockerConfig(device as Button, channel as ButtonChannel);
      await device.updateAllFromKnxMetadataImmediately();

      if (channel.buttonSide == ButtonSide.left) {
        //
        // MWE 7.3.2025
        //
        // Beim Splitten eines Buttons entsteht ein bislang ungenutzter neuer ButtonChannel.
        // Den Type diese neuen Request-Button-Channels übernehmen vom KnxChannel.
        //
        // Dieser hat dabei zunächst den "DefaultChannel Type" laut KnxProducktDaten.
        //
        // Wenn wir das nicht tun würden,
        // hätten wir oft einen Mismatch des RequestChannelTpes zum KnxChannelType,
        // da der rechte Button unter umständen nur eine eingeschränken Range von Typen unterstützt.
        //
        const rightButtonChannelTag = channel.tag.substring(0, 3) + "R";
        const rightButtonChannel = device.channels.findFirst((bc) => bc.tag == rightButtonChannelTag);

        if (rightButtonChannel && rightButtonChannel.knxChannel) {
          //
          // 30.10.2025
          // Bevorzugt den Type des am Rocker bereits angeschlossenen Consumers übernehmen,
          // statt immer den willkürlichen "DefaultChannelType" laut KnxProduktdaten zu setzen.
          // Nur wenn kein Consumer verbunden ist, oder dessen Type für diesen Kanal nicht
          // unterstützt wird, greift weiterhin der KnxChannel-Default.
          //
          const connectedConsumerType = channel.getConnectedConsumer()?.channelType;
          const supportsConsumerType =
            connectedConsumerType != undefined &&
            rightButtonChannel.allowedChannelTypes?.some((option) => option.channelType === connectedConsumerType);

          rightButtonChannel.channelType = supportsConsumerType
            ? connectedConsumerType!
            : rightButtonChannel.knxChannel?.channelType;
        }
      }

      channel.connections.forEach((con) => {
        const partner = con.getPartner(channel);
        if (partner) {
          let allowed1 = true;
          if (channel.knxChannel) {
            allowed1 = areChannelTypesCompatible(
              channel.channelType,
              channel.knxChannel.channelType,
              device,
            );
          }

          con.forbidden = allowed1 && !AllowedConections.isConnectionAllowed(channel, partner, false);
          con.raiseOnChange("forbidden");
        }
      });
    }

    //          const atleastOneParamChanged = device.template.knxProductData.currentRules.pullAllValues(device, {});

    // if (atleastOneParamChanged) {
    //   await device.updateAllFromKnxMetadata();
    // }
  }

  device.channels.raiseOnChange("buttonSide");
  device.raiseOnChange("childCollection");

}
